import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap, finalize, timeout } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { isTokenExpired } from '../shared/utils/jwt-token.util';

/**
 * Reactive token refresh — the fallback path.
 *
 * The proactive timer in AuthService is what normally keeps a session alive; this
 * interceptor only runs when that timer was missed (sleep/wake, a throttled background
 * tab, a hard reload inside the expiry gap, or a race).
 *
 *   request ──▶ 401 ──▶ handle401Error
 *                          │
 *              ┌───────────┴───────────┐
 *              │ first 401             │ concurrent 401s
 *              ▼                       ▼
 *        POST /auth/refresh      wait on refreshTokenSubject
 *              │                       │
 *       ┌──────┴──────┐                └──▶ retry with the new token
 *       ▼             ▼
 *    success        failure ──▶ clear tokens, /login
 *       │                        (subject COMPLETED, never errored)
 *       └──▶ store, publish, retry the original request
 *
 * Module-level state, deliberately: it must be shared across every request in the app so
 * a burst of 401s produces exactly one refresh. That also makes it long-lived, which is
 * why the teardown rules below matter.
 */
let isRefreshing = false;
let refreshTokenSubject = new BehaviorSubject<string | null>(null);

/** How long a queued request waits for the in-flight refresh before giving up. */
const QUEUE_TIMEOUT_MS = 30_000;

export const jwtInterceptor: HttpInterceptorFn = (request: HttpRequest<any>, next: HttpHandlerFn) => {
  const router = inject(Router);

  // Add auth header if user is logged in and request is to our API
  if (isLoggedIn() && isApiUrl(request)) {
    const token = getAccessToken();
    if (token) {
      request = addTokenHeader(request, token);
    }
  }

  return next(request).pipe(
    catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401 && isApiUrl(request)) {
        return handle401Error(request, next, router);
      }
      return throwError(() => error);
    })
  );
};

// Helper functions that directly access localStorage to avoid circular dependency
function isLoggedIn(): boolean {
  const token = localStorage.getItem('accessToken');
  return token !== null && !isTokenExpired(token);
}

function getAccessToken(): string | null {
  return localStorage.getItem('accessToken');
}

function getRefreshToken(): string | null {
  return localStorage.getItem('refreshToken');
}

function isApiUrl(request: HttpRequest<any>): boolean {
  return request.url.startsWith(environment.apiUrl);
}

function addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({
    headers: request.headers.set('Authorization', `Bearer ${token}`)
  });
}

/**
 * Release everyone waiting on the current refresh and arm a fresh subject.
 *
 * Two things here are load-bearing:
 *
 * 1. COMPLETE, never error. Queued requests are surfaced through BaseService, which
 *    toasts every error — erroring would fire one toast per queued request, stacking N
 *    toasts over the login screen. Completing lets each queued request finish silently,
 *    which is right because the only caller that completes without a token is also
 *    navigating to /login.
 * 2. Settle the OLD subject, THEN swap in a new one. Requests already parked in
 *    handle401Error captured the old reference; reassigning first would strand them
 *    forever. And the subject is module-level, so an errored one would reject every
 *    future subscriber for the lifetime of the page — a dead session app-wide.
 */
function resetRefreshState(): void {
  const previous = refreshTokenSubject;
  refreshTokenSubject = new BehaviorSubject<string | null>(null);
  previous.complete();
  isRefreshing = false;
}

/** Wipe local credentials and send the user to the login screen. */
function forceLogin(router: Router): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  router.navigate(['/login']);
}

/**
 * True when a failed refresh should NOT end the session.
 *
 * Only status 0 (the request never reached the server, or the connection dropped before
 * any response) is safe to treat as transient. A 5xx is NOT: AuthService rotates the
 * refresh token in place — `refreshTokenEntity.TokenHash = ComputeTokenHash(new)` — so
 * the server may have committed the rotation before the response was lost. Treating that
 * as retryable would send the now-dead old token and get a 401, i.e. cause exactly the
 * forced logout this whole change exists to prevent.
 */
function isTransientFailure(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 0;
}

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  router: Router
): Observable<HttpEvent<any>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      resetRefreshState();
      forceLogin(router);
      return throwError(() => new Error('Authentication required'));
    }

    // Manually call refresh token endpoint to avoid circular dependency
    const refreshRequest = new HttpRequest('POST', `${environment.apiUrl}/auth/refresh`, {
      refreshToken: refreshToken
    });

    return next(refreshRequest).pipe(
      // next() emits HttpEventType.Sent before the response. Without this filter the
      // first event reaches switchMap with body === undefined, throws 'Invalid refresh
      // response', and catchError logs the user out — the original bug.
      filter((event): event is HttpResponse<any> => event instanceof HttpResponse),
      take(1),
      switchMap((response: HttpResponse<any>) => {
        const responseBody = response.body;
        if (responseBody && responseBody.success && responseBody.data) {
          const newAccessToken = responseBody.data.accessToken;
          const newRefreshToken = responseBody.data.refreshToken;

          localStorage.setItem('accessToken', newAccessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          refreshTokenSubject.next(newAccessToken);

          return next(addTokenHeader(request, newAccessToken));
        }
        throw new Error('Invalid refresh response');
      }),
      catchError((error) => {
        // A transient network drop must not end a session that is still valid on the
        // server. The next 401 starts a fresh refresh attempt.
        if (!isTransientFailure(error)) {
          forceLogin(router);
        }
        return throwError(() => error);
      }),
      // Runs on success, on error, AND on unsubscribe. That last case is the one that
      // matters: this refresh lives inside the 401'd request's subscription, and every
      // page here uses takeUntil(destroy$), so navigating away mid-refresh used to strand
      // isRefreshing === true forever — after which every later 401 parked on the queue
      // below and hung behind a permanent spinner with no error and no recovery.
      finalize(() => resetRefreshState())
    );
  }

  return refreshTokenSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    // Never wait on the in-flight refresh indefinitely.
    timeout(QUEUE_TIMEOUT_MS),
    switchMap((token) => next(addTokenHeader(request, token)))
  );
}
